/**
 * Friction factor correlations for pipe flow.
 *
 * Single-phase:
 *   Laminar (Re < 2300): f = 64/Re
 *   Turbulent: Colebrook-White (solved via Haaland approximation)
 *
 * Two-phase:
 *   Homogeneous multiplier: φ²_lo based on mixture properties
 */

/**
 * Darcy friction factor for single-phase flow.
 *
 * @param Re Reynolds number (ρuD/μ)
 * @param roughness Absolute roughness [m]
 * @param diameter Pipe diameter [m]
 * @returns Darcy friction factor (dimensionless)
 */
export function darcyFriction(Re: number, roughness: number, diameter: number): number {
  if (Re <= 0) return 0;

  if (Re < 2300) {
    // Laminar: f = 64 / Re
    return 64 / Re;
  }

  if (Re < 4000) {
    // Transition zone: C1-continuous cubic blend of the local laminar and
    // turbulent correlations. The smoothstep weight has zero slope at both
    // endpoints, so f and df/dRe match 64/Re at Re=2300 and Haaland at
    // Re=4000 without the derivative jumps of the old linear interpolation.
    const f_lam = 64 / Re;
    const f_turb = haaland(Re, roughness, diameter);
    const t = (Re - 2300) / (4000 - 2300);
    const cubicWeight = t * t * (3 - 2 * t);
    return f_lam * (1 - cubicWeight) + f_turb * cubicWeight;
  }

  // Turbulent: Haaland approximation to Colebrook-White
  return haaland(Re, roughness, diameter);
}

/**
 * Haaland equation — explicit approximation to Colebrook-White.
 * Accurate to within 1.5% of Moody chart.
 *
 * 1/√f = -1.8 log₁₀[(ε/D/3.7)^1.11 + 6.9/Re]
 */
function haaland(Re: number, roughness: number, diameter: number): number {
  const epsD = roughness / diameter;
  const term = Math.pow(epsD / 3.7, 1.11) + 6.9 / Re;
  const invSqrtF = -1.8 * Math.log10(term);
  return 1 / (invSqrtF * invSqrtF);
}

/**
 * Reynolds number.
 *
 * @param rho Density [kg/m³]
 * @param u Velocity [m/s]
 * @param D Diameter [m]
 * @param mu Dynamic viscosity [Pa·s]
 */
export function reynolds(rho: number, u: number, D: number, mu: number): number {
  if (mu <= 0) return 0;
  return Math.abs(rho * u * D / mu);
}

/**
 * Friction pressure gradient for single-phase pipe flow.
 *
 * dP_friction/dx = f · ρ · u · |u| / (2D)
 *
 * @param f Darcy friction factor
 * @param rho Density [kg/m³]
 * @param u Velocity [m/s]
 * @param D Diameter [m]
 * @returns Signed pressure-loss gradient [Pa/m], odd in velocity. A caller
 *          subtracts this term so it always opposes the current flow direction.
 */
export function frictionGradient(f: number, rho: number, u: number, D: number): number {
  return f * rho * u * Math.abs(u) / (2 * D);
}

/**
 * Two-phase friction multiplier (homogeneous model).
 *
 * φ²_lo = (1 + x(ρ_f/ρ_g - 1)) × (1 + x(μ_f/μ_g - 1))^(-0.25)
 *
 * @param x Vapour quality (0-1)
 * @param rho_f Saturated liquid density [kg/m³]
 * @param rho_g Saturated vapour density [kg/m³]
 * @param mu_f Liquid viscosity [Pa·s]
 * @param mu_g Vapour viscosity [Pa·s]
 * @returns Two-phase multiplier (≥1)
 */
export function twoPhaseMultiplier(
  x: number, rho_f: number, rho_g: number, mu_f: number, mu_g: number
): number {
  if (x <= 0 || x >= 1) return 1;
  const densityTerm = 1 + x * (rho_f / rho_g - 1);
  const viscosityTerm = Math.pow(1 + x * (mu_f / mu_g - 1), -0.25);
  return Math.max(1, densityTerm * viscosityTerm);
}

/**
 * Homogeneous mixture density for two-phase flow.
 *
 * 1/ρ_m = x/ρ_g + (1-x)/ρ_f
 */
export function mixtureDensity(x: number, rho_f: number, rho_g: number): number {
  if (x <= 0) return rho_f;
  if (x >= 1) return rho_g;
  return 1 / (x / rho_g + (1 - x) / rho_f);
}

/**
 * Homogeneous mixture viscosity (linear interpolation).
 */
export function mixtureViscosity(x: number, mu_f: number, mu_g: number): number {
  if (x <= 0) return mu_f;
  if (x >= 1) return mu_g;
  return (1 - x) * mu_f + x * mu_g;
}

/**
 * Void fraction from quality using homogeneous model.
 *
 * α = 1 / (1 + (1-x)/x × ρ_g/ρ_f)
 */
export function voidFraction(x: number, rho_f: number, rho_g: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return 1 / (1 + ((1 - x) / x) * (rho_g / rho_f));
}

/**
 * Gravitational pressure gradient.
 *
 * dP_gravity/dx = ρ · g · sin(θ)
 *
 * @param rho Density [kg/m³]
 * @param theta Inclination angle [rad] (positive = uphill)
 * @returns Pressure gradient [Pa/m] (positive for uphill flow)
 */
export function gravityGradient(rho: number, theta: number): number {
  return rho * 9.80665 * Math.sin(theta);
}
